import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FlaskConical, Paperclip, ListChecks, Package } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";

import { createProject, listProjects, type PdevMeta, type ProjectView } from "@/api/product-dev";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { STAGE_STYLES, apiErr } from "@/pages/product-dev/format";

interface Props {
  meta: PdevMeta;
  onOpen: (projectId: string) => void;
}

/** Доска проектов, сгруппированная по стадиям (читаемый статус = решение боли передачи дел). */
export function ProjectList({ meta, onOpen }: Props): React.ReactElement {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const projects = useQuery({ queryKey: ["pdev", "projects"], queryFn: () => listProjects() });

  const create = useMutation({
    mutationFn: () => createProject({ name: name.trim() }),
    onSuccess: (p) => {
      setName("");
      qc.invalidateQueries({ queryKey: ["pdev", "projects"] });
      toast.success("Проект создан");
      onOpen(p.id);
    },
    onError: (e) => toast.error(apiErr(e, "Не удалось создать проект")),
  });

  const stageKeys = Object.keys(meta.stages);
  const byStage = (stage: string): ProjectView[] =>
    (projects.data ?? []).filter((p) => p.stage === stage);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FlaskConical className="size-6 text-primary" />
          <h1 className="text-2xl font-bold">Разработка продукции</h1>
        </div>
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) create.mutate();
          }}
        >
          <Input
            placeholder="Название новинки…"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-64"
          />
          <Button type="submit" disabled={!name.trim() || create.isPending}>
            Новый проект
          </Button>
        </form>
      </div>

      {projects.isLoading ? (
        <div className="text-sm text-muted-foreground">Загрузка…</div>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-3">
          {stageKeys.map((stage) => {
            const items = byStage(stage);
            return (
              <div key={stage} className="w-72 shrink-0">
                <div className="mb-2 flex items-center justify-between px-1">
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${STAGE_STYLES[stage] ?? ""}`}
                  >
                    {meta.stages[stage]}
                  </span>
                  <span className="text-xs text-muted-foreground">{items.length}</span>
                </div>
                <div className="space-y-2">
                  {items.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => onOpen(p.id)}
                      className="w-full rounded-lg border bg-card p-3 text-left transition-colors hover:border-primary/50 hover:bg-accent"
                    >
                      <div className="font-medium leading-tight">{p.name}</div>
                      {p.category && (
                        <div className="mt-0.5 text-xs text-muted-foreground">{p.category}</div>
                      )}
                      <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="inline-flex items-center gap-1" title="Выходы BOM">
                          <Package className="size-3.5" />
                          {p.output_count ?? 0}
                        </span>
                        <span className="inline-flex items-center gap-1" title="Вложения">
                          <Paperclip className="size-3.5" />
                          {p.attachment_count ?? 0}
                        </span>
                        <span
                          className="inline-flex items-center gap-1"
                          title="Открытых пунктов чек-листа"
                        >
                          <ListChecks className="size-3.5" />
                          {p.open_checklist_count ?? 0}
                        </span>
                        {p.owner_username && <span className="ml-auto">{p.owner_username}</span>}
                      </div>
                    </button>
                  ))}
                  {items.length === 0 && (
                    <div className="rounded-lg border border-dashed p-3 text-center text-xs text-muted-foreground">
                      —
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
